import { useState } from "react";
import { findStarter, MILESTONE_TEXT, type MilestoneKey } from "../../../shared/milestones";
import { rarityLabel } from "../../../shared/rarity";
import { StarterPicker } from "../../components/cards/StarterPicker";
import { useCardCollection } from "../../components/cards/CardCollection";
import { useI18n } from "../../i18n/I18nProvider";
import { api } from "../../lib/api";

/**
 * Mi colección: the starter's 16 cards (locked ones face down), the milestones that unlock them and
 * which one is the avatar — any unlocked card, always the latest, or their own photo.
 */
export function CollectionSection() {
  const { t, locale } = useI18n();
  const { collection, update } = useCardCollection();
  const [changing, setChanging] = useState(false);
  const [error, setError] = useState(false);

  if (!collection) return null;
  const line = findStarter(collection.starter);
  const choose = async (choice: Parameters<typeof api.setAvatarChoice>[0]) => {
    setError(false);
    try {
      update(await api.setAvatarChoice(choice));
    } catch {
      setError(true);
    }
  };

  return (
    <section className="collection" aria-labelledby="collection-title">
      <div className="section-head">
        <h2 id="collection-title" className="section-title">
          {t("cards.title")}
        </h2>
        {line && (
          <p className="collection-level">
            {t("cards.level", { level: collection.level, max: collection.maxLevel })} · {line.name}
          </p>
        )}
      </div>
      {error && <p className="form-error">{t("cards.pickError")}</p>}

      {!line || changing ? (
        <>
          <p className="muted">{line ? t("cards.changeIntro") : t("cards.chooseIntro")}</p>
          <StarterPicker
            current={collection.starter}
            onPick={async (key) => {
              update(await api.chooseStarter(key));
              setChanging(false);
            }}
          />
          {changing && (
            <button type="button" className="btn btn-link" onClick={() => setChanging(false)}>
              {t("cards.cancel")}
            </button>
          )}
        </>
      ) : (
        <>
          <div className="collection-actions">
            {collection.mode === "photo" ? (
              <button type="button" className="btn btn-outline btn-sm" onClick={() => choose({ mode: "card", level: null })}>
                {t("cards.useCard")}
              </button>
            ) : (
              <>
                {collection.hasPhoto && (
                  <button type="button" className="btn btn-outline btn-sm" onClick={() => choose({ mode: "photo" })}>
                    {t("cards.usePhoto")}
                  </button>
                )}
                {collection.avatarLevel !== collection.level && (
                  <button type="button" className="btn btn-outline btn-sm" onClick={() => choose({ mode: "card", level: null })}>
                    {t("cards.useLatest")}
                  </button>
                )}
              </>
            )}
            <button type="button" className="btn btn-link btn-sm" onClick={() => setChanging(true)}>
              {t("cards.changeStarter")}
            </button>
          </div>

          <ol className="collection-grid">
            {collection.cards.map((c) => (
              <li key={c.level} className={c.id ? (collection.mode === "card" && collection.avatarLevel === c.level ? "is-avatar" : undefined) : "is-locked"}>
                {c.image ? (
                  <button type="button" className="collection-card" title={t("cards.useThis")} onClick={() => choose({ mode: "card", level: c.level })} aria-label={`${t("cards.level", { level: c.level, max: collection.maxLevel })}: ${c.name} (${rarityLabel(c.rarity, locale) ?? c.rarity}). ${t("cards.useThis")}`}>
                    <img src={c.image} alt="" width={160} height={223} loading="lazy" />
                  </button>
                ) : (
                  <span className="collection-card card-back" aria-label={t("cards.locked", { level: c.level })} />
                )}
                <small>
                  {t("cards.levelShort", { level: c.level })}
                  {c.rarity ? ` · ${rarityLabel(c.rarity, locale) ?? c.rarity}` : ""}
                </small>
              </li>
            ))}
          </ol>
        </>
      )}

      <h3 className="collection-subtitle">{t("cards.milestones", { done: collection.milestones.filter((m) => m.reachedAt).length, total: collection.milestones.length })}</h3>
      <ul className="milestone-list">
        {collection.milestones.map((m) => (
          <li key={m.key} className={m.reachedAt ? "is-done" : undefined}>
            <span aria-hidden="true">{m.reachedAt ? "✓" : "○"}</span> {MILESTONE_TEXT[m.key as MilestoneKey]?.[locale] ?? m.key}
            {m.reachedAt && <span className="sr-only"> ({t("cards.done")})</span>}
          </li>
        ))}
      </ul>
    </section>
  );
}
